import {useEffect, useMemo, useState} from "react";
import {Plus, ChevronRight, UserPlus, Send, Users, MessageCircle, Lock, Clock, Check, X} from "lucide-react";
import {LIFESTYLE_TOPICS} from "@/lib/constants";
import {apiGet, apiPost} from "@/lib/api";
import CreateCircleModal from "@/pages/modals/CreateCircle";
import {useSite, Rich} from "@/lib/site";

function JoinRequests({circle, notify}) {
  const [reqs, setReqs] = useState([]);
  useEffect(() => { apiGet(`/communities/${circle.community_id}/requests`).then(setReqs).catch(() => setReqs([])); }, [circle.community_id]);
  const decide = async (uid, action) => {
    try { await apiPost(`/communities/${circle.community_id}/requests/${uid}/${action}`, {}); setReqs(r => r.filter(x => x.user_id !== uid)); notify(action === "approve" ? "Member approved" : "Request declined"); }
    catch { notify("Please try again"); }
  };
  if (!reqs.length) return null;
  return (
    <div className="join-requests" data-testid="circle-join-requests">
      <div className="eyebrow">JOIN REQUESTS ({reqs.length})</div>
      {reqs.map(r => (
        <div key={r.user_id} className="admin-row" data-testid={`join-request-${r.user_id}`}>
          <div><strong>{r.user_name || r.user_id}</strong><small>Asked {new Date(r.joined_at).toLocaleDateString()}</small></div>
          <button data-testid={`join-approve-${r.user_id}`} className="coral-button sm" onClick={() => decide(r.user_id, "approve")}><Check size={13}/> Approve</button>
          <button data-testid={`join-reject-${r.user_id}`} className="outline-button sm" onClick={() => decide(r.user_id, "reject")}><X size={13}/></button>
        </div>
      ))}
    </div>
  );
}

export default function Circles({communities, setCommunities, notify, user, requireAuth, openMember, focus}) {
  const t = useSite();
  const [topic, setTopic] = useState("All");
  const [active, setActive] = useState(null);
  useEffect(() => {
    if (focus && !active) setActive(communities.find(c => c.community_id === focus) || null);
  }, [focus, communities]); // eslint-disable-line react-hooks/exhaustive-deps
  const [messages, setMessages] = useState([]);
  const [locked, setLocked] = useState(false);
  const [text, setText] = useState("");
  const [create, setCreate] = useState(false);

  const topics = useMemo(() => ["All", ...LIFESTYLE_TOPICS], []);
  const filtered = useMemo(() => communities.filter(c => topic === "All" || c.topic === topic), [communities, topic]);
  const isOwner = active && user && (user.is_admin || active.created_by === user.user_id);

  useEffect(() => {
    if (!active) return;
    setLocked(false);
    apiGet(`/communities/${active.community_id}/messages`).then(setMessages).catch(() => { setMessages([]); setLocked(true); });
  }, [active, user]);

  const send = async (e) => {
    e.preventDefault();
    if (!text || !active) return;
    requireAuth(async () => {
      try {
        const r = await apiPost(`/communities/${active.community_id}/messages`, {text});
        setMessages(m => [...m, r]); setText("");
      } catch { notify(locked ? "Join this samaj to post" : "Please try again"); }
    });
  };

  const join = () => requireAuth(async () => {
    try {
      const r = await apiPost(`/communities/${active.community_id}/join`, {});
      const next = {...active, my_status: r.status};
      setActive(next);
      setCommunities(prev => prev.map(c => c.community_id === next.community_id ? next : c));
      notify(r.status === "pending" ? "Request sent — the creator will approve it" : `Padharo! You joined ${active.name}`);
    } catch { notify("Please try again"); }
  });

  const status = active?.my_status;
  const joinLabel = isOwner ? "You manage this" : status === "member" ? "Member" : status === "pending" ? "Request pending" : active?.type === "Private" ? "Ask to join" : "Join";

  return (
    <div className="two-pane" data-testid="circles-page">
      <div className="page-title">
        <div>
          <div className="eyebrow">SAMAJ · APNA LOKO</div>
          <h1><Rich text={t("circles_title")}/></h1>
          <p>{t("circles_sub")}</p>
        </div>
        <button data-testid="circles-create" className="coral-button" onClick={() => requireAuth(() => setCreate(true))}>
          <Plus size={16}/> Create samaj
        </button>
      </div>

      <div className="two-pane-grid">
        <aside className="topic-rail" data-testid="circles-topic-rail">
          <div className="eyebrow">LIFESTYLE SUBJECTS</div>
          {topics.map(t => (
            <button key={t} data-testid={`topic-${t.replace(/\s+/g,"-").toLowerCase()}`} className={topic === t ? "topic-row active" : "topic-row"} onClick={() => { setTopic(t); setActive(null); }}>
              <span>{t}</span><ChevronRight size={13}/>
            </button>
          ))}
        </aside>

        <section className="topic-main">
          {!active ? (
            <>
              <div className="results-toolbar"><strong data-testid="circles-count">{filtered.length} samaj in {topic}</strong></div>
              {filtered.length === 0 && (
                <div className="empty-pane">
                  <Users size={28}/>
                  <h3>No samaj in {topic} yet</h3>
                  <p>Be the first to start one — admin will review and launch it for everyone.</p>
                  <button data-testid="empty-circles-create" className="coral-button" onClick={() => requireAuth(() => setCreate(true))}><Plus size={15}/> Create samaj</button>
                </div>
              )}
              <div className="community-list">
                {filtered.map(c => (
                  <article key={c.community_id} className="community-row" data-testid={`circle-row-${c.community_id}`}>
                    <span className={`circle-dot ${c.color || "coral"}`}/>
                    <div>
                      <div className="row-meta">
                        <span className="pill">{c.type}{c.type === "Private" && <Lock size={11} style={{marginLeft: 4}}/>}</span>
                        {c.topic && <span className="pill ghost">{c.topic}</span>}
                        {c.my_status && <span className="pill ghost" data-testid={`circle-status-${c.community_id}`}>{c.my_status === "member" ? "Joined" : "Pending"}</span>}
                      </div>
                      <h3>{c.name}</h3>
                      <p>{c.description}</p>
                      <small>{c.members || 1} members</small>
                    </div>
                    <button data-testid={`circle-open-${c.community_id}`} className="outline-button sm" onClick={() => setActive(c)}>Open <ChevronRight size={14}/></button>
                  </article>
                ))}
              </div>
            </>
          ) : (
            <div className="chat-view">
              <div className="chat-head">
                <div>
                  <button data-testid="circle-back" className="back-btn" onClick={() => setActive(null)}>← Back</button>
                  <div className="eyebrow">{active.type} samaj · {active.topic}</div>
                  <h2>{active.name}</h2>
                  <p>{active.description}</p>
                </div>
                <button data-testid="circle-join" className="coral-button" disabled={!!status || isOwner} onClick={join}>
                  {status === "pending" ? <Clock size={15}/> : <UserPlus size={15}/>} {joinLabel}
                </button>
              </div>
              {isOwner && active.type === "Private" && <JoinRequests circle={active} notify={notify}/>}
              {locked ? (
                <div className="empty-chat" data-testid="circle-locked">
                  <Lock size={25}/>
                  <p>This is a private samaj. {status === "pending" ? "Your request is waiting for the creator's approval." : "Ask to join to see the conversation."}</p>
                </div>
              ) : (
                <>
                  <div className="chat-messages">
                    {messages.length === 0 ? (
                      <div className="empty-chat"><MessageCircle size={25}/><p>Open the conversation with your samaj.</p></div>
                    ) : messages.map(m => (
                      <div key={m.message_id} className="chat-message" data-testid={`circle-message-${m.message_id}`}>
                        <div className="avatar small">{m.author?.[0] || "M"}</div>
                        <div>
                          <button className="author-link" data-testid={`message-author-${m.message_id}`} onClick={() => m.user_id && openMember(m.user_id)}>{m.author}</button>
                          <p>{m.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {status === "member" || isOwner ? (
                    <form className="chat-compose" onSubmit={send}>
                      <input data-testid="circle-message-input" value={text} onChange={e => setText(e.target.value)} placeholder="Share something with your samaj..."/>
                      <button data-testid="circle-message-send" className="coral-button" type="submit"><Send size={16}/></button>
                    </form>
                  ) : (
                    <div className="join-to-comment" data-testid="circle-join-to-comment">
                      <p>Join this samaj to comment and get notified of new posts.</p>
                      <button data-testid="circle-join-inline" className="coral-button sm" disabled={status === "pending"} onClick={join}>{status === "pending" ? "Request pending" : active.type === "Private" ? "Ask to join" : "Join samaj"}</button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </section>
      </div>

      {create && <CreateCircleModal close={() => setCreate(false)} notify={notify}/>}
    </div>
  );
}
